import { isPortrait } from '@src/js/common/utils';
import type EditingStateManager from '@src/slide/application/service/editing-state-manager';
import EditProcess from '@src/slide/application/service/edit-action-services/edit-process';
import { IMoveElementManager } from '@src/slide/application/service/move-element-manager/move-element-manager.interface';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { SelectedEleMgrType } from '@src/slide/domain/selection/constant/select-element-type';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import ObserverFactory from '@src/slide/application/service/modelObserver/observerFactory';
import { TDWActionType, TDWModuleType, TDWOperationType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import { ADSORP_ANGLE, ADSORP_RANGE, calculateOffsetAngle, computeAngleReaminder, IPoint } from '@src/slide/infrastructure/module/independent/graphic/rotate';
import { observable } from 'mobx';
import { IRotateElementHandler } from './rotate-element-handler.interface';
import { IConnectorModel } from '@src/slide/domain/ui-model/element-modeles/Interface/connector-model.interface';
import { centerPointOfRect } from '@src/slide/infrastructure/module/independent/graphic';
import { ILinkManager } from '@src/slide/application/service/link-manager/core/link-manager.interface';
import { Nullable } from '@src/slide/infrastructure/module/independent/types';
import ElementInfoAdapter from '../element-info';
import { IGroupModel } from '@src/slide/domain/ui-model/element-modeles/Interface/group-model.interface';
import { EDIT_PANEL_ID } from '@src/framework/editor/src/page/index/components/canvas/canvas-constants';

export default class RotateElementHandler implements IRotateElementHandler {
    /**
     * 是否触发了双指旋转
     */
    public doubleFingerRotating = false;
    /**
     * 移动端是否进入旋转模式
     */
    @observable
    public isMobileRotatingMode = false;
    /**
     * 当前操作元素
     */
    private elementModel?: IElementBaseModel;

    /**
     * 通用编辑之后逻辑
     */
    private editProcessLogic?: EditProcess;

    /**
     * 是否在组合内
     */
    private isGroupChildren: boolean;
    private slideTopLeft: IPoint;

    /**
     * 当前缩放比值
     */
    private scale: number;
    /**
     * 记录的是当前元素相对slide的中心点
     */
    private centerPointOnSlide: IPoint | null;
    /**
     * 当前rotate
     */
    private rotate: number;
    /**
     * 记录选中的元素的实际rotate，不包含吸附修正
     */
    private elementRotates: number[] = [];

    /**
     * 双指位置缓存
     */
    private cachedTouches: TouchList;
    /**
     * 记录上次旋转角度与初始旋转角度的差值
     */
    private lastOffsetAngle: number;
    /**
    * 鼠标初始位置
    */
    private mouseStartPostion: IPoint | null;
    /**
     * 选中的元素数据
     */
    private selectedUiModels: IElementBaseModel[] | null;
    /**
     * 记录组合子元素的旋转角度受父元素翻转的影响，应该是顺时针改变还是逆时针改变
     */
    private groupChildrenDirections: number[] | null;
    /**
     * 是否按下shift键
     */
    private isShiftKey: boolean;
    /**
     * 记录组合子元素style存储的rotate与元素相对于slide的rotate差值
     */
    private groupChildrenOffsetRotates: number[] | null;

    // eslint-disable-next-line no-useless-constructor
    public constructor(
        private editingStateManager: EditingStateManager,
        private moveElementManager: IMoveElementManager,
        private selectionService: ISelectionService,
        private linkManager: ILinkManager,
    ) {}

    /**
     * rotate开始逻辑
     */
    public handleRotateStart = (elementModel: IElementBaseModel, scale: number, e?: TouchEvent): void => {
        this.mouseStartPostion = this.getEventCoordinates(e);
        this.lastOffsetAngle = 0;
        this.elementModel = elementModel;
        this.isGroupChildren = elementModel?.parent?.type === UIModelType.GROUP;
        this.scale = scale;
        this.rotate = elementModel.getFrame().getRotate();
        this.slideTopLeft = this.getSlideTopLeft();
        this.editProcessLogic = new EditProcess(this.editingStateManager, elementModel);
        this.editProcessLogic.setSelectionService(this.selectionService);
        if (this.isGroupChildren) {
            this.handleRotateStartForRootGroup();
            return;
        }
        this.linkManager.linkFloatBarService.hide(); // 关闭链接浮层
        this.moveElementManager.checkIsDelayUpdatePosing();
        this.editingStateManager.updateElementRotateState(true);
        const { selectedElements } = this.selectionService;
        this.selectedUiModels = selectedElements.selectedArrayForType(SelectedEleMgrType.SLIDE);
        // 记录当前选中元素的rotate
        this.setElementRotates(this.selectedUiModels);
        if (!this.isGroupChildren) {
            selectedElements.clear(SelectedEleMgrType.GROUP);
        }

        if (browser.isMobile && e?.touches) {
            this.cachedTouches = e.touches;
        }
        this.report();
    };

    /**
     * 双指rotate过程中逻辑
     */
    public handleDoubleFingersRotate = (e: TouchEvent): void => {
        // 组内元素先不支持双指
        if (this.isGroupChildren) {
            return;
        }
        const element  = this.elementModel;
        this.rotate = this.rotate ?? element?.props.style.rotate ?? 0;
        if (element) {
            let rotateAngle = this.rotateAngleWidthDomItemAndPointForDoubleFingers(
                this.elementModel,
                e,
                this.cachedTouches,
                this.rotate,
            );
            rotateAngle = (rotateAngle + 360) % 360;
            const offsetAngle = (rotateAngle - this.rotate + 360) % 360;
            this.handleRotateForSelectedModels(offsetAngle);
            this.cachedTouches = e.touches;
        }
    };

    /**
     * rotate过程中逻辑
     */
    public handleRotate = (e: TouchEvent): void => {
        this.isShiftKey = !!(e?.shiftKey);
        if (this.isGroupChildren) {
            this.handleRotateForRootGroup(e);
            return;
        }
        const element  = this.elementModel;
        if (element) {
            const centerPoint = {
                x: element.props.style.left + (element.props.style.width / 2),
                y: element.props.style.top + (element.props.style.height / 2),
            };
            const angle = this.getMouseAngleChangeRelativeToCenterPoint(e, centerPoint);
            const currentOffsetAngle = (angle - this.lastOffsetAngle + 360) % 360;
            this.lastOffsetAngle = angle;
            this.handleRotateForSelectedModels(currentOffsetAngle);
        }
    };

    /**
     * rotate过程中逻辑，直接传递变化的rotate
     */
    public updateRotateWhileRotating = (rotate: number): void => {
        if (!this.selectedUiModels) return;
        this.clearConnectorStartAndEnd();
        const { length } = this.selectedUiModels;
        for (let i = 0; i < length; i++) {
            const direction = this.groupChildrenDirections?.[i] ?? 1;
            const element = this.selectedUiModels[i];
            if (element.type === UIModelType.TABLE) {
                continue;
            } else {
                element.props.style.rotate = ((rotate * direction) + 360) % 360;
            }
            this.notifyRotate(element);
            this.handleRelatedModel(element);
        }
        this.rotate = (this.rotate + rotate) % 360;
    };

    /**
     * rotate结束逻辑
     */
    public handleRotateStop = (): void => {
        this.lastOffsetAngle = 0;
        this.selectedUiModels = null;
        this.mouseStartPostion = null;
        if (this.isGroupChildren) {
            this.handleRotateStopForRootGroup();
            return;
        }
        this.editingStateManager.updateElementRotateState(false);
        this.editProcessLogic?.persistSize('');
        this.editProcessLogic?.updateOperationFrame(null);
        window.dispatchEvent(new Event('resize')); // 检查是否需要resize，这里必须要加。因为frame没有变更
    };

    /**
     * 组合内元素rotate开始逻辑
     */
    public handleRotateStartForRootGroup = (): void => {
        this.moveElementManager.checkIsDelayUpdatePosing();
        this.editingStateManager.updateElementRotateState(true);
        this.selectedUiModels = this.selectionService.selectedElements.selectedArrayForType(SelectedEleMgrType.GROUP);
        this.groupChildrenDirections = ElementInfoAdapter.getGroupChildrenDirections(this.selectedUiModels);
        this.groupChildrenOffsetRotates = this.selectedUiModels
            .map(model => (model.parent as IGroupModel).getFrame().getRotate(true));
        this.report();
        // 记录当前选中元素的rotate
        this.setElementRotates(this.selectedUiModels);
    };

    /**
     * 组合内元素rotate过程中逻辑
     * @param e
     */
    public handleRotateForRootGroup = (e: TouchEvent): void => {
        const element = this.elementModel;
        if (element) {
            if (!this.centerPointOnSlide) {
                const frameOnSlide = element.getFrame?.().frameOnSlide();
                this.centerPointOnSlide = centerPointOfRect(frameOnSlide) ?? null;
            }
            const angle = this.getMouseAngleChangeRelativeToCenterPoint(e, this.centerPointOnSlide!);
            const currentOffsetAngle = (angle - this.lastOffsetAngle + 360) % 360;
            this.lastOffsetAngle = angle;
            this.handleRotateOfSelectedForRoot(currentOffsetAngle);
        }
    };

    /**
     * 组合内元素rotate结束逻辑
     */
    public handleRotateStopForRootGroup = (): void => {
        this.editingStateManager.updateElementRotateState(false);
        this.editProcessLogic?.persistSizeForGroup(this.elementModel?.rootGroupModel);
        this.editProcessLogic?.updateOperationFrame(null);
        this.centerPointOnSlide = null;
        this.groupChildrenDirections = null;
        this.groupChildrenOffsetRotates = null;
    };

    public setIsMobileRotatingMode(mode: boolean): void {
        if (!isPortrait() && this.isMobileRotatingMode !== mode) {
            window.dispatchEvent(new Event('resize'));
        }
        this.isMobileRotatingMode = mode;
    }

    /**
     * 计算吸附角度：在整数倍吸附角度的吸附范围内，进行吸附
     */
    private getAdsorpAngle = (rotateAngle: number, adsorpRange: number, adsorpAngel: number) => {
        if (this.isShiftKey) {
            return Math.floor(rotateAngle / 15) * 15;
        }
        let newAngle = rotateAngle;
        const rotateAngleRemainder = computeAngleReaminder(newAngle, adsorpAngel, adsorpRange);
        const isAbsorb = Math.abs(rotateAngleRemainder) <= adsorpRange;
        if (isAbsorb) {
            if (rotateAngleRemainder > 0) {
                newAngle = Math.floor(rotateAngle / adsorpAngel) * adsorpAngel;
            } else if (rotateAngleRemainder < 0) {
                newAngle = (Math.floor(rotateAngle / adsorpAngel) + 1) * adsorpAngel;
            }
        }
        return newAngle;
    };

    private setElementRotates(selectedUiModels: IElementBaseModel[]) {
        this.elementRotates = [];
        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let i = 0; i < selectedUiModels.length; i++) {
            if (!selectedUiModels[i].props.style.rotate) {
                this.elementRotates.push(0);
            } else {
                this.elementRotates.push(selectedUiModels[i].props.style.rotate ?? 0);
            }
        }
    }

    private handleRotateForSelectedModels(offsetAngle: number) {
        if (!this.selectedUiModels) return;
        this.clearConnectorStartAndEnd();
        const { length } = this.selectedUiModels;
        for (let i = 0; i < length; i++) {
            const resultElement = this.selectedUiModels[i];
            const resuleElementRotate = resultElement.getFrame().getRotate();
            let rotateAngle = this.elementRotates[i];
            if (resultElement.type === UIModelType.TABLE) {
                continue;
            } else {
                if (resultElement.type === UIModelType.CONNECTOR) {  // 线条在多选中正常旋转
                    resultElement.props.style.rotate = (resuleElementRotate + offsetAngle) % 360;
                } else {
                    rotateAngle = (rotateAngle + offsetAngle) % 360;
                    // 计算吸附后的旋转角度
                    const adsorpRotateAngle = this.getAdsorpAngle(rotateAngle, ADSORP_RANGE, ADSORP_ANGLE);
                    resultElement.props.style.rotate = (adsorpRotateAngle + 360) % 360;
                    this.elementRotates[i] = rotateAngle;
                }
            }
            this.notifyRotate(resultElement);
            this.handleRelatedModel(resultElement);
        }
        this.rotate = (this.rotate + offsetAngle) % 360;
    }

    private handleRotateOfSelectedForRoot(currentOffsetAngle: number) {
        if (!this.selectedUiModels || !this.groupChildrenDirections) return;
        this.clearConnectorStartAndEnd();
        const { length } = this.selectedUiModels;
        let offsetAngle = currentOffsetAngle;
        for (let i = 0; i < length; i++) {
            const resultElement = this.selectedUiModels[i];
            const dir = this.groupChildrenDirections[i];
            offsetAngle = currentOffsetAngle * dir;
            const resuleElementRotate = resultElement.getFrame().getRotate();
            const rotateAngle = this.elementRotates[i];
            if (resultElement.type === UIModelType.CONNECTOR) {
                resultElement.props.style.rotate = (resuleElementRotate + offsetAngle) % 360;
            } else {
                const styleAngle = (rotateAngle + offsetAngle) % 360;
                const angleOnSlide = this.getAngleOnSlide(i, styleAngle);
                const adsorpAngleOnSlide = this.getAdsorpAngle(angleOnSlide, ADSORP_RANGE, ADSORP_ANGLE);
                // 计算吸附后的旋转角度
                const adsorpStyleAngle = this.convertSlideAngleToSelf(i, adsorpAngleOnSlide);
                resultElement.props.style.rotate = (adsorpStyleAngle + 360) % 360;
                // 存储未做吸附调整的旋转角度
                this.elementRotates[i] = styleAngle % 360;
            }
            this.notifyRotate(resultElement);
            this.handleRelatedModel(resultElement);
        }
        this.rotate = (this.rotate + offsetAngle) % 360;
    }

    /**
     * 上报所需的action
     */
    private getAction() {
        if (this.selectedUiModels?.length === 1) { // 单选元素
            const [model] = this.selectedUiModels;
            switch (model.type) {
                case UIModelType.SHAPE:
                    return TDWActionType.SHAPE_WHIRL;
                case UIModelType.IMAGE:
                    return TDWActionType.IMG_WHIRL;
            }
        }
        return '';
    }

    /**
     * 上报tdw
     */
    private report() {
        const action = this.getAction();
        if (action) {
            tdw({
                opername: TDWOperationType.DOC_PPT,
                module: TDWModuleType.PC_OBJ,
                action,
            });
        }
    }

    private notifyRotate = (element: IElementBaseModel) => {
        const observer = ObserverFactory.createObserver(element);
        observer?.notifyLocalChange();
    };

    private isElementInTargetGroup(element, groupElement) {
        if (!element.parent) {
            return false;
        }
        if (element.parent?.key === groupElement.key) {
            return true;
        }
        return this.isElementInTargetGroup(element.parent, groupElement);
    }

    private handleRelatedModel = (element: IElementBaseModel, rootElement?: IElementBaseModel) => {
        if (!rootElement) {
            rootElement = element;
        }
        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let j = 0; j < element.relatedUIModelRefs.length; j++) {
            const relatedElement = element.relatedUIModelRefs[j];
            if (this.isElementInTargetGroup(relatedElement, rootElement)) {
                continue;
            }
            const updateSytle = relatedElement.connectorCaculater.getUpdateStyle({
                rotate: element.props.style.rotate,
            });
            relatedElement.updateModelStyle(updateSytle);
            const observer = ObserverFactory.createObserver(relatedElement);
            observer?.notifyLocalChange();
        }
        // 组合元素需要递归处理与子元素关联的 组合外的连接线
        if (element.type === UIModelType.GROUP) {
            element.children.forEach((model) => {
                this.handleRelatedModel(model as IElementBaseModel, rootElement);
            });
        }
    };

    private getSlideTopLeft() {
        const slideDiv = document.getElementById(EDIT_PANEL_ID);
        const slideRect = slideDiv?.getBoundingClientRect();
        return {
            x: slideRect?.left ?? 0,
            y: slideRect?.top ?? 0,
        };
    }
    /**
     * 双指旋转计算角度偏移量
     */
    private rotateAngleWidthDomItemAndPointForDoubleFingers(
        elementModel: Nullable<IElementBaseModel>,
        e: TouchEvent,
        oldTouches: TouchList,
        oldAngle: number,
    ) {
        let angle = oldAngle;
        if (elementModel) {
            // 根据双指旋转拿到偏移量
            if (e?.touches?.length === 2) {
                // 根据新的touches拿到当前双指角度
                const { touches } = e;

                // 得到第二组两个点
                const newAngle = this.getTouchesAngle(touches[0], touches[1]);

                // 拿到上一次操作的双指角度
                const oldAngle = this.getTouchesAngle(oldTouches[0], oldTouches[1]);

                // 计算角度偏移，加到元素原本的角度上
                const angelOffset = newAngle - oldAngle;
                angle = Math.round(angle + angelOffset);
                angle = angle % 360;
            }
        }
        return angle;
    }

    private getTouchesAngle(point1, point2) {
        const x = point1.pageX - point2.pageX;
        const y = point1.pageY - point2.pageY;
        return Math.atan2(y, x) * 180 / Math.PI;
    }

    /**
    * 获取鼠标相对于中心点的角度变化
    */
    private getMouseAngleChangeRelativeToCenterPoint(e, centerPoint: IPoint): number {
        if (!this.mouseStartPostion) return 0;
        // 中心点，相对于视图
        const pCenter = {
            x: (centerPoint.x * this.scale) + this.slideTopLeft.x,
            y: (centerPoint.y * this.scale) + this.slideTopLeft.y,
        };
        const currentPos = this.getEventCoordinates(e);
        return calculateOffsetAngle(this.mouseStartPostion, pCenter, currentPos!);
    }

    private getEventCoordinates = (e?): IPoint | null => (e ? {
        x: e.touches ? e.touches[0].clientX : e.clientX,
        y: e.touches ? e.touches[0].clientY : e.clientY,
    } : null);

    /**
     * 自身角度转换为相对slide的角度
     */
    private getAngleOnSlide = (index: number, styleAngle: number) => {
        const diffRotate = this.groupChildrenOffsetRotates![index];
        const dir = this.groupChildrenDirections![index];
        return (diffRotate + (styleAngle * dir)) % 360;
    };

    /**
     * 相对于slide的角度转换为自身角度
     */
    private convertSlideAngleToSelf = (index: number, slideAngle: number) => {
        const diffRotate = this.groupChildrenOffsetRotates![index];
        const dir = this.groupChildrenDirections![index];
        return (((slideAngle - diffRotate) / dir) + 360) % 360;
    };

    /**
     * 连接线清除连接
     */
    private clearConnectorStartAndEnd() {
        this.selectedUiModels?.forEach((model) => {
            if (model.type === UIModelType.CONNECTOR) {
                (model as IConnectorModel).clearStartAndEnd();
            }
        });
    }
}
